import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable dialog + trigger for "Change password" used by both customer and driver.
 *
 * Props:
 *   endpoint: backend path, e.g. "/api/customer/me/password" or "/api/driver/me/password"
 *   tokenKey: localStorage key, e.g. "customer_token" or "driver_token"
 *   trigger:  optional render prop for custom button; defaults to a full-width outline button
 */
export default function PasswordChangeDialog({ endpoint, tokenKey, trigger, testId = "change-password-btn" }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}${endpoint}`,
        { current_password: current, new_password: next },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem(tokenKey)}` },
          validateStatus: () => true,
        }
      );
      const data = res.data || {};
      if (res.status < 200 || res.status >= 300) { toast.error(data?.detail || "Could not update password"); return; }
      toast.success("Password updated");
      setOpen(false);
      setCurrent(""); setNext(""); setConfirm("");
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <Button
          onClick={() => setOpen(true)}
          variant="outline"
          className="w-full py-5 rounded-xl"
          data-testid={testId}
        >
          <Lock className="w-4 h-4 mr-2" /> Change password
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="password-change-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>
              Enter your current password and choose a new one (min 6 characters).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Current password</label>
              <Input
                type="password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">New password</label>
              <Input
                type="password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Confirm new password</label>
              <Input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={6}
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading} className="bg-orange-500 hover:bg-orange-600" data-testid="submit-password-change-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                Update password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
